import * as React from "react";
import {
	Alert,
	Autocomplete,
	Box,
	Button,
	Card,
	Chip,
	FormControl,
	Radio,
	Stack,
	Tab,
	Tabs,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { PaperclipIcon } from "@phosphor-icons/react/dist/ssr/Paperclip";
import { toast } from "sonner";

import { languageMatches } from "@/lib/languages";
import type {
	CampaignRequestAgent,
	CampaignRequestAgentPayload,
	CampaignRequestRate,
	DocumentKind,
	PriceCode,
	RequestAgentKind,
} from "@/hooks/api/use-campaign-requests";
import {
	useCampaignRequestRates,
	useCreateCampaignRequestAgent,
	useDeleteCampaignRequestAgent,
	useUpdateCampaignRequestAgent,
	useUploadCampaignRequestDocument,
} from "@/hooks/api/use-campaign-requests";
import type { CatalogueVoice } from "@/hooks/api/use-voices";
import { useVoiceCatalogue } from "@/hooks/api/use-voices";
import type { FilterOption } from "@/components/customTable/types";
import { FilterFetchSelect } from "@/components/fetch-select";

import {
	AGENT_KIND_OPTIONS,
	formatBytes,
	formatMoney,
	languageLabel,
	marketFlag,
	marketLabel,
	MARKETS,
	MAX_DOCUMENT_BYTES,
} from "../constants";
import { AGENT_NAME_SEPARATOR, isDefaultAgentName } from "../view/agent-naming";
import { sameStrings, sameText } from "./dirty";
import { VoicePicker } from "./voice-picker";
import type { StepProps } from "./wizard-shell";
import { StepHeading, useWizardDirty, WizardFooter } from "./wizard-shell";

/**
 * Step 2 — agents. The step that decides what this costs.
 *
 * One tab per *language*, never per market. A single English agent answers both Singapore and
 * Australia, so pricing it per market would bill the same build twice; the markets an agent serves
 * ride along on its tab instead.
 *
 * Tabs rather than a stack of cards, because three languages stacked is a scroll: the client sets
 * English up, scrolls past it forever to reach Bahasa Indonesia, and never sees the two side by
 * side. The tab strip is also the only place the state of a language the client is *not* looking at
 * can be shown, which is what the outstanding marker is for. The running setup total sits in the
 * footer, outside the strip, because it is the total for the step and not for one language.
 *
 * Prices sit on the options themselves, because this is where scope creep is either priced or
 * given away: an "update the agent" request showing a number next to it is a decision, while the
 * same request in an email is free work. The running total in the footer is what stops anyone
 * reaching the quote and being surprised by it.
 */

interface AgentDraft {
	/** The persisted row, or null where this language has never been saved. */
	id: string | null;
	kind: RequestAgentKind;
	markets: string[];
	label: string;
	sourceAgentId: string | null;
	/** Only for the picker — the API stores the id and resolves the name itself. */
	sourceAgentLabel: string;
	callFlowDocumentId: string | null;
	/** The curated voice this language is getting, or null while nobody has chosen. */
	voiceId: string | null;
	/** "None of these voices", in the client's own words. Empty string where there is no ask. */
	voiceRequestNote: string;
}

/** Which document kind a choice attaches. A revision is a rework, and the API keeps them apart. */
function callFlowKind(kind: RequestAgentKind): DocumentKind {
	return kind === "agent_revision" ? "revised_call_flow" : "call_flow";
}

/** The three choices that only mean anything against an agent that already exists. */
function needsSourceAgent(kind: RequestAgentKind): boolean {
	return kind === "reuse_existing" || kind === "regional_deployment" || kind === "agent_revision";
}

function needsCallFlow(kind: RequestAgentKind): boolean {
	return kind === "agent_setup" || kind === "agent_revision";
}

/** The API's own ceiling on `label` — a name we suggest has to fit inside it. */
const MAX_LABEL = 255;

/** The placeholder a draft is created with. It is not a name anybody chose. */
const UNTITLED_CAMPAIGN = "Untitled campaign";

/**
 * The name we suggest for a language's agent: the campaign, then the language it speaks.
 *
 * It used to be the bare language, and everywhere the name is read back — the pill on step 3, the
 * line on the quote — "English" reads as a language rather than as the name of a thing. Putting
 * the campaign in front of it makes the name a name.
 *
 * An unnamed draft falls back to the language alone: "Untitled campaign — English" is our own
 * placeholder repeated back at the client. It regenerates the moment they name the campaign.
 */
function defaultLabel(title: string | null | undefined, language: string): string {
	const spoken = languageLabel(language);
	const campaign = (title ?? "").trim();

	if (campaign.length === 0 || campaign === UNTITLED_CAMPAIGN) return spoken;

	// A campaign name may itself be 255 characters, and the API refuses a longer label than that.
	// The campaign is what gets cut: a name that has lost its language has lost the half that
	// tells two agents on the same campaign apart.
	const room = MAX_LABEL - AGENT_NAME_SEPARATOR.length - spoken.length;
	const fitted = campaign.length > room ? campaign.slice(0, room).trimEnd() : campaign;

	return `${fitted}${AGENT_NAME_SEPARATOR}${spoken}`;
}

/**
 * What this language is still waiting on, named.
 *
 * The tab strip shows a marker against a language the client is not looking at, and a marker with
 * nothing behind it is a scold — so the same list is what the marker's tooltip reads out. None of
 * these blocks the step: a call flow is due before we build, not before we quote, and a voice can
 * be agreed in preparation.
 */
function outstandingFor(draft: AgentDraft, hasVoices: boolean): string[] {
	const items: string[] = [];

	if (draft.markets.length === 0) items.push("Markets it serves");
	if (needsSourceAgent(draft.kind) && !draft.sourceAgentId) items.push("Which agent to start from");
	if (needsCallFlow(draft.kind) && !draft.callFlowDocumentId) items.push("Call flow");
	// Only where there is something to choose from. A language we curate no voices for is ours to
	// settle in preparation, and marking it here would be marking the client for our own gap.
	if (hasVoices && !draft.voiceId && draft.voiceRequestNote.trim().length === 0) items.push("Voice");

	return items;
}

/**
 * The rate for an agent charge, as this account sees it.
 *
 * Read from `GET /campaign-requests/:id/rates`, never from `/price-book`: that table is
 * cross-account and super-admin only, so a client's read of it is a 403 — which used to strip
 * every price off this step and leave the footer reading "Setup so far —". The rates endpoint
 * answers the narrow question instead, already resolved through the account's own overrides.
 *
 * A code can still be absent: a rate nobody has agreed yet is withheld rather than shown, because
 * quoting refuses to price against a placeholder and a number the quote will not honour is worse
 * than none. The option says so on its chip and the banner above says why, once for the step.
 *
 * Agent charges are per language and never market-specific — one English agent answers Singapore
 * and Australia — so only the market-less rate is ever the right one here.
 */
function rateFor(rates: CampaignRequestRate[] | undefined, code: PriceCode | null): string | null {
	if (!code || !rates) return null;
	return rates.find((rate) => rate.code === code && rate.market === null)?.amount ?? null;
}

export function StepAgents({ request, goTo, exit, readOnly }: StepProps): React.JSX.Element {
	const languages = React.useMemo(() => request.languages ?? [], [request.languages]);
	const markets = React.useMemo(() => request.markets ?? [], [request.markets]);

	const { data: rates, isPending: ratesPending } = useCampaignRequestRates(request.id);
	const currency = rates?.[0]?.currency ?? "SGD";

	/**
	 * Every active voice, once, grouped per language here rather than fetched per card.
	 *
	 * The catalogue is a platform-level list of four or five voices per language — a request with
	 * four languages would otherwise make four round trips to render one step. `languageMatches`
	 * is the server's own rule mirrored: an `en` voice answers an `en-SG` agent.
	 */
	const {
		data: catalogue,
		isLoading: voicesLoading,
		isError: voicesFailed,
	} = useVoiceCatalogue({ enabled: languages.length > 0 });

	const voicesByLanguage = React.useMemo(() => {
		const grouped = new Map<string, CatalogueVoice[]>();

		for (const language of languages) {
			grouped.set(
				language,
				(catalogue ?? []).filter((voice) => languageMatches(voice.language, language))
			);
		}

		return grouped;
	}, [catalogue, languages]);

	const createMutation = useCreateCampaignRequestAgent();
	const updateMutation = useUpdateCampaignRequestAgent();
	const deleteMutation = useDeleteCampaignRequestAgent();
	const uploadMutation = useUploadCampaignRequestDocument();

	const [drafts, setDrafts] = React.useState<Record<string, AgentDraft>>(() => seed());
	const [busy, setBusy] = React.useState(false);

	/** Which language is on screen. Every language's draft stays mounted in `drafts` regardless. */
	const [activeLanguage, setActiveLanguage] = React.useState<string>(() => languages[0] ?? "");

	// A language dropped on step 1 must not leave the strip pointing at a tab that no longer exists.
	React.useEffect(() => {
		if (languages.length > 0 && !languages.includes(activeLanguage)) setActiveLanguage(languages[0]);
	}, [activeLanguage, languages]);

	/**
	 * Every chosen language gets a card whether or not it has been saved yet. The first language is
	 * assumed to be the one the call flow is written in; the rest start as translations of it,
	 * which is the shape almost every multi-market campaign actually takes.
	 */
	function seed(): Record<string, AgentDraft> {
		const next: Record<string, AgentDraft> = {};

		for (const [index, language] of languages.entries()) {
			const saved = request.agents.find((agent) => agent.language === language);

			/**
			 * A name the client typed is theirs and is never regenerated. A name still in the shape
			 * we suggested follows the campaign, so renaming the campaign on step 1 renames the
			 * agents that were named after it.
			 *
			 * Two cases keep exactly what is stored. A settled request, because nothing here can be
			 * written back to it; and a request holding a live quote, because rewriting an agent runs
			 * `recompute()` and withdraws that quote — a cosmetic rename is not worth costing the
			 * client the price they are on their way to confirm. Editing the campaign name is itself
			 * what supersedes a quote, so the case this defers is only ever an old bare-language name
			 * on a request whose title has not moved since.
			 */
			const settled = readOnly || request.currentQuote !== null;
			const theirName = saved && (settled || !isDefaultAgentName(saved.label, language)) ? saved.label : null;

			next[language] = {
				id: saved?.id ?? null,
				kind: saved?.kind ?? (index === 0 ? "agent_setup" : "translated_agent"),
				// One market on the request is not a choice, so it is not asked as one: the agent
				// gets it, and the card states it rather than offering a picker with one option.
				markets: saved?.markets?.length ? saved.markets : markets.length === 1 || languages.length === 1 ? markets : [],
				label: theirName ?? defaultLabel(request.title, language),
				sourceAgentId: saved?.sourceAgentId ?? null,
				sourceAgentLabel: "",
				callFlowDocumentId: saved?.callFlowDocumentId ?? null,
				voiceId: saved?.voiceId ?? null,
				voiceRequestNote: saved?.voiceRequestNote ?? "",
			};
		}

		return next;
	}

	// Languages change on step 1, and a card has to appear for a language added after this step was
	// last opened. Reseeding from the saved rows is safe: everything on a card is already persisted.
	// The campaign name is in here for the same reason — an agent still carrying the name we
	// suggested is renamed with the campaign it was named after, and is then written on Continue.
	React.useEffect(() => {
		const reseeded = seed();
		opened.current = reseeded;
		setDrafts(reseeded);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- `seed` closes over exactly these.
	}, [request.agents, request.title, request.currentQuote, languages, markets, readOnly]);

	const patch = (language: string, changes: Partial<AgentDraft>): void => {
		setDrafts((current) => ({ ...current, [language]: { ...current[language], ...changes } }));
	};

	const setupTotal = React.useMemo(() => {
		let total = 0;
		let priced = 0;

		for (const language of languages) {
			const draft = drafts[language];
			if (!draft) continue;

			const option = AGENT_KIND_OPTIONS.find((candidate) => candidate.value === draft.kind);
			// Reuse has no price code at all rather than a zero rate, so it is counted as priced.
			if (!option?.priceCode) {
				priced += 1;
				continue;
			}

			const amount = rateFor(rates, option.priceCode);
			if (amount === null) continue;

			total += Number(amount);
			priced += 1;
		}

		return { total, complete: priced === languages.length };
	}, [drafts, languages, rates]);

	const handleUpload = async (language: string, file: File): Promise<void> => {
		const draft = drafts[language];
		if (!draft) return;

		if (file.size > MAX_DOCUMENT_BYTES) {
			toast.error(`${file.name} is over the ${formatBytes(MAX_DOCUMENT_BYTES)} limit`);
			return;
		}

		try {
			const uploaded = await uploadMutation.mutateAsync({
				requestId: request.id,
				kind: callFlowKind(draft.kind),
				file,
				// Which agent this script is for, said out loud. Agents are keyed by language, so a
				// request with an English build and a Bahasa build has two rows both waiting on a
				// flow — and the API cannot tell them apart from the file alone. Null while this
				// card has never been saved, in which case there is no row to bind to yet and the
				// id below is written by `save()` a moment later.
				agentId: draft.id,
			});
			patch(language, { callFlowDocumentId: uploaded.id });
		} catch {
			// 413 over the ceiling, 415 where the bytes are not what this kind may be; both toast.
		}
	};

	/** What this card would be written as, and what it is compared against to see if it moved. */
	const payloadFor = React.useCallback(
		(language: string, draft: AgentDraft): CampaignRequestAgentPayload => ({
			language,
			markets: draft.markets,
			kind: draft.kind,
			// An emptied name is not a nameless agent: the API refuses a blank label, and the quote
			// line has to say something. It goes back to what we would have suggested.
			label: draft.label.trim() || defaultLabel(request.title, language),
			sourceAgentId: needsSourceAgent(draft.kind) ? draft.sourceAgentId : null,
			callFlowDocumentId: needsCallFlow(draft.kind) ? draft.callFlowDocumentId : null,
			voiceId: draft.voiceId,
			/**
			 * Kept even once a voice is chosen. The note is the record of what the client asked
			 * for, and the blocker it raises answers itself from `voiceId` — so clearing it here
			 * would delete the ask rather than close it.
			 */
			voiceRequestNote: draft.voiceRequestNote.trim() || null,
		}),
		[request.title]
	);

	/**
	 * Whether the row behind a card already says exactly what the card says.
	 *
	 * One comparison, used by both the dirty flag and the save loop. Two copies of it is how a step
	 * ends up warning about unsaved work it will then decline to write — or, worse, writing on
	 * Continue when nothing moved and withdrawing the client's own live quote.
	 */
	const matchesSaved = React.useCallback(
		(payload: CampaignRequestAgentPayload, draft: AgentDraft, saved: CampaignRequestAgent | undefined): boolean =>
			saved !== undefined &&
			saved.kind === payload.kind &&
			saved.label === payload.label &&
			saved.sourceAgentId === payload.sourceAgentId &&
			saved.callFlowDocumentId === payload.callFlowDocumentId &&
			saved.voiceId === payload.voiceId &&
			sameText(draft.voiceRequestNote, saved.voiceRequestNote) &&
			sameStrings(saved.markets, draft.markets),
		[]
	);

	/**
	 * What the cards looked like the moment this step opened.
	 *
	 * A language picked on step 1 gets a card here before it has an agent row behind it, so
	 * comparing only against saved rows reported "unsaved changes" on mount, before the client had
	 * touched anything — which trains people to click Discard, and that is precisely when it eats
	 * a real edit. An untouched seeded card is not a change; it is a suggestion the client has not
	 * answered yet.
	 */
	const opened = React.useRef(drafts);

	/** Whether a card on screen differs from the row behind it, or a language has gone away. */
	const dirty = React.useMemo(() => {
		const moved = languages.some((language) => {
			const draft = drafts[language];
			if (!draft) return false;

			const saved = draft.id ? request.agents.find((agent) => agent.id === draft.id) : undefined;
			if (saved) return !matchesSaved(payloadFor(language, draft), draft, saved);

			// Never saved: dirty only once the client has moved it away from what we suggested.
			const asOpened = opened.current[language];
			return !asOpened || !sameDraft(draft, asOpened);
		});

		return moved || request.agents.some((agent) => !languages.includes(agent.language));
	}, [drafts, languages, matchesSaved, payloadFor, request.agents]);

	// The rail is the one way off this step that writes nothing, so it has to ask first.
	useWizardDirty(dirty);

	/**
	 * Writes every card that moved, adds the ones that are new and drops the languages that went
	 * away.
	 *
	 * Unchanged cards are skipped rather than rewritten. Every nested write here comes back through
	 * `recompute()` on the API, which withdraws a live quote and sends the request back for
	 * re-pricing — so passing back through this step to look at something must not cost the client
	 * the quote they are on their way to confirm. See `./dirty`.
	 */
	const save = async (): Promise<boolean> => {
		setBusy(true);

		try {
			for (const language of languages) {
				const draft = drafts[language];
				if (!draft) continue;

				const payload = payloadFor(language, draft);

				const saved = draft.id ? request.agents.find((agent) => agent.id === draft.id) : undefined;

				if (matchesSaved(payload, draft, saved)) continue;

				await (draft.id
					? updateMutation.mutateAsync({ requestId: request.id, agentId: draft.id, data: payload })
					: createMutation.mutateAsync({ requestId: request.id, data: payload }));
			}

			// A language dropped on step 1 leaves its agent behind; the API nulls any market that
			// pointed at it rather than refusing, so removing it here is safe.
			for (const agent of request.agents) {
				if (!languages.includes(agent.language)) {
					await deleteMutation.mutateAsync({ requestId: request.id, agentId: agent.id });
				}
			}

			return true;
		} catch {
			return false;
		} finally {
			setBusy(false);
		}
	};

	const handleContinue = async (): Promise<void> => {
		if (await save()) goTo(3);
	};

	const handleSaveExit = async (): Promise<void> => {
		if (await save()) exit();
	};

	/** One language's agent, in full. Only the language on the active tab is mounted. */
	const renderAgent = (language: string, tabbed: boolean): React.JSX.Element | null => {
		const draft = drafts[language];
		if (!draft) return null;

		const attached = request.documents.find((document) => document.id === draft.callFlowDocumentId);

		return (
			<Card variant="outlined" sx={{ p: 2 }}>
				<Stack spacing={2}>
					{/*
					 * The name, first and largest, because this card *is* the agent and this is what it
					 * is called everywhere it is read back — the pill on step 3, the line on the quote.
					 * No label above it either: a field at the top of a card reading "Poly Q3 — English"
					 * is a title, and a title with "What we should call this agent" written over it is
					 * no longer a title. Where there is no tab strip to name the language, the line
					 * underneath says it, because a renamed agent stops carrying it.
					 */}
					<TextField
						fullWidth
						disabled={readOnly}
						value={draft.label}
						onChange={(event) => patch(language, { label: event.target.value })}
						placeholder={defaultLabel(request.title, language)}
						slotProps={{ htmlInput: { "aria-label": "Agent name", maxLength: MAX_LABEL } }}
						sx={{ "& .MuiInputBase-input": { fontSize: "1.15rem", fontWeight: 600 } }}
						helperText={
							tabbed
								? "Appears on your schedule and on your quote."
								: `Your ${languageLabel(language)} agent. The name appears on your schedule and on your quote.`
						}
					/>

					{/* The markets this one agent answers. Several markets, one agent, one charge.
					    Offered only where there is genuinely more than one to choose between — with a
					    single market it is not a choice, and the card states it at the foot instead. */}
					{markets.length > 1 ? (
						<Autocomplete
							multiple
							disableCloseOnSelect
							disabled={readOnly}
							size="small"
							sx={{ maxWidth: 520 }}
							options={MARKETS.filter((market) => markets.includes(market.value))}
							getOptionLabel={(option) => `${option.flag} ${option.label}`}
							value={MARKETS.filter((market) => draft.markets.includes(market.value))}
							onChange={(_event, selected) => patch(language, { markets: selected.map((market) => market.value) })}
							renderInput={(params) => <TextField {...params} label="Markets it serves" />}
						/>
					) : null}

					<Stack spacing={1}>
						{AGENT_KIND_OPTIONS.map((option) => {
							const amount = rateFor(rates, option.priceCode);
							const selected = draft.kind === option.value;

							return (
								<Card
									key={option.value}
									variant="outlined"
									sx={{
										borderColor: selected ? "primary.main" : "divider",
										cursor: readOnly ? "default" : "pointer",
										opacity: readOnly && !selected ? 0.6 : 1,
										p: 1.5,
									}}
									// A card is not a form control, so a disabled fieldset would not stop it —
									// the handler has to refuse for itself.
									onClick={readOnly ? undefined : () => patch(language, { kind: option.value })}
								>
									<Stack direction="row" spacing={1.5} sx={{ alignItems: "flex-start" }}>
										<Radio checked={selected} disabled={readOnly} size="small" sx={{ p: 0, mt: 0.25 }} />

										<Box sx={{ flex: 1, minWidth: 0 }}>
											<Typography variant="subtitle2">{option.label}</Typography>
											<Typography variant="body2" color="text.secondary">
												{option.help}
											</Typography>
										</Box>

										{/*
										 * The price is on the option, not on a summary page after the decision —
										 * and it stays on every tab, because the choice is made here.
										 *
										 * Where no rate is agreed yet the chip says so in its own words rather
										 * than repeating the option's name back at it, which is what it used to
										 * do: "New agent setup" beside "Build a new agent" is one fact twice.
										 */}
										<Chip
											color={option.priceCode === null ? "success" : "default"}
											label={
												option.priceCode === null
													? "No charge"
													: amount === null
														? "On your quote"
														: formatMoney(amount, currency)
											}
											size="small"
											variant={option.priceCode === null ? "soft" : "outlined"}
										/>
									</Stack>
								</Card>
							);
						})}
					</Stack>

					{needsSourceAgent(draft.kind) ? (
						<FormControl fullWidth>
							<FilterFetchSelect
								label="Which of your agents?"
								showInputLabel
								disabled={readOnly}
								placeholder="Select an agent"
								value={{ label: draft.sourceAgentLabel, value: draft.sourceAgentId ?? "" } as FilterOption}
								onChange={(selected: FilterOption | string) => {
									const value = typeof selected === "string" ? selected : ((selected.value as string) ?? "");
									const label = typeof selected === "string" ? selected : ((selected.label as string) ?? "");
									patch(language, { sourceAgentId: value || null, sourceAgentLabel: label });
								}}
								getOptionsUrl="/agents"
								fetchLabel="name"
								fetchValue="id"
							/>
						</FormControl>
					) : null}

					{needsCallFlow(draft.kind) ? (
						<Box>
							<Button
								component="label"
								variant="outlined"
								size="small"
								startIcon={<PaperclipIcon />}
								disabled={readOnly || uploadMutation.isPending}
							>
								{draft.kind === "agent_revision" ? "Upload the revised call flow" : "Upload your call flow"}
								<input
									type="file"
									hidden
									accept=".pdf,.docx,.txt"
									onChange={(event) => {
										const file = event.target.files?.[0];
										event.target.value = "";
										if (file) void handleUpload(language, file);
									}}
								/>
							</Button>

							{/* The attached file is a filename and a size, which is a line rather than a
							    tag — and "uploaded" beside a file that is plainly attached is a third
							    saying of the same fact. */}
							{attached ? (
								<Typography variant="caption" color="success.main" sx={{ display: "block", mt: 0.5 }}>
									{attached.originalFilename} · {formatBytes(attached.sizeBytes)}
								</Typography>
							) : (
								<Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
									{"We can quote and confirm without it — the call flow only has to be here before we build."}
								</Typography>
							)}
						</Box>
					) : null}

					{/* How this agent sounds. One line at rest, the cards only once asked for. */}
					<VoicePicker
						language={language}
						voices={voicesByLanguage.get(language) ?? []}
						loading={voicesLoading}
						failed={voicesFailed}
						readOnly={readOnly}
						value={{ voiceId: draft.voiceId, requestNote: draft.voiceRequestNote }}
						onChange={(choice) => patch(language, { voiceId: choice.voiceId, voiceRequestNote: choice.requestNote })}
					/>

					{/*
					 * Where the picker above is on screen it already lists the markets, and printing
					 * them again underneath is the same fact twice. This says something only in the
					 * two cases the picker cannot: nothing attached, or nothing to attach it from.
					 */}
					{draft.markets.length === 0 ? (
						<Typography variant="caption" color="warning.main">
							No markets attached yet. This agent will not answer anywhere until it has at least one.
						</Typography>
					) : markets.length === 1 ? (
						<Typography variant="caption" color="text.secondary">
							Answers {marketFlag(markets[0])} {marketLabel(markets[0])}
						</Typography>
					) : null}
				</Stack>
			</Card>
		);
	};

	if (languages.length === 0) {
		return (
			<Stack spacing={3}>
				<StepHeading title="Which agents will make these calls?" />
				<Alert severity="warning">
					No languages have been chosen yet. Go back to campaign basics and say which languages these calls happen in —
					one agent is built per language, not per market.
				</Alert>
				<WizardFooter onBack={() => goTo(1)} onContinue={() => goTo(1)} continueLabel="Back to basics" />
			</Stack>
		);
	}

	return (
		<Stack spacing={3}>
			<StepHeading
				title="Which agents will make these calls?"
				help="One per language. Each choice affects your quote — the running total updates as you go."
			/>

			{/* Only where a rate is genuinely missing — a rate nobody has agreed yet is withheld
			    rather than shown, so the option keeps its written hint and says why. */}
			{ratesPending || setupTotal.complete ? null : (
				<Alert severity="info">
					A few of these are still being priced. You see the figure on your quote at step 7.
				</Alert>
			)}

			{/*
			 * One tab per language. Two languages is the first point at which a strip earns its
			 * place; a strip of one is a heading with a line under it, so a single language is drawn
			 * as the card it always was.
			 */}
			{languages.length > 1 ? (
				<Box sx={{ borderBottom: "1px solid var(--mui-palette-divider)" }}>
					<Tabs
						value={activeLanguage}
						variant="scrollable"
						onChange={(_event, value: string) => setActiveLanguage(value)}
					>
						{languages.map((language) => {
							const draft = drafts[language];
							const outstanding = draft ? outstandingFor(draft, (voicesByLanguage.get(language) ?? []).length > 0) : [];

							return (
								<Tab
									key={language}
									value={language}
									tabIndex={0}
									sx={{ minHeight: "auto" }}
									label={
										// A tab is a button, so everything in its label is phrasing content.
										<Stack component="span" direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
											<span>{languageLabel(language)}</span>

											{/*
											 * The state of a language the client is not looking at, in the only
											 * space there is for it. A dot rather than a count: the number is
											 * meaningless without the list, and the list is one hover away.
											 */}
											{outstanding.length > 0 ? (
												<Tooltip title={`Still to do: ${outstanding.join(" · ")}`}>
													<Box
														component="span"
														aria-label={`Still to do: ${outstanding.join(", ")}`}
														sx={{ bgcolor: "warning.main", borderRadius: "50%", height: 8, width: 8 }}
													/>
												</Tooltip>
											) : null}
										</Stack>
									}
								/>
							);
						})}
					</Tabs>
				</Box>
			) : null}

			{renderAgent(languages.length > 1 ? activeLanguage : languages[0], languages.length > 1)}

			<WizardFooter
				busy={busy}
				leading={
					<Box sx={{ textAlign: { sm: "right" } }}>
						<Typography variant="subtitle2">
							Setup so far {setupTotal.complete ? formatMoney(setupTotal.total, currency) : "—"}
						</Typography>
						<Typography variant="caption" color="text.secondary">
							{setupTotal.complete ? "Fixed. Confirmed on the quote." : "Priced in full on step 7."}
						</Typography>
					</Box>
				}
				onBack={() => goTo(1)}
				onSaveExit={readOnly ? undefined : () => void handleSaveExit()}
				onContinue={readOnly ? () => goTo(3) : () => void handleContinue()}
			/>
		</Stack>
	);
}

/** Field-by-field equality for an unsaved card, so an untouched suggestion does not read as an edit. */
function sameDraft(a: AgentDraft, b: AgentDraft): boolean {
	return (
		a.kind === b.kind &&
		a.label === b.label &&
		a.sourceAgentId === b.sourceAgentId &&
		a.callFlowDocumentId === b.callFlowDocumentId &&
		a.voiceId === b.voiceId &&
		sameText(a.voiceRequestNote, b.voiceRequestNote) &&
		sameStrings(a.markets, b.markets)
	);
}
